import { Layout } from "../../../components/Layout";
import { ReactComponent as Logo } from "../../../assets/LogoIcon.svg";
import { ReactComponent as ArrowLeft } from "../../../assets/ArrowLeft.svg";
import { ReactComponent as UploadIcon } from "../../../assets/DownloadFileLine.svg";
import { ReactComponent as AWSLogo } from "../../../assets/AWSLogo.svg";
import { ReactComponent as AzureLogo } from "../../../assets/AzureLogo.svg";
import { ReactComponent as SalesforceLogo } from "../../../assets/SalesforceLogo.svg";
import { ReactComponent as DownloadFile } from "../../../assets/DownloadFileLine.svg";
import { ReactComponent as Trash } from '../../../assets/Trash.svg'
import styles from './two.module.css';
import { useState, useRef } from 'react';
import clsx from "clsx";
import React from "react";
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { formatFormulation, FormattedFormulation } from './utils';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';

interface StepTwoProps {
    onNext: (uploadedFiles: File[], title: string) => void;
    onBack: () => void;
    formulation: string;
    generatedCode: string;
    isLoading?: boolean;
    processingMessage?: string;
}

const StepTwo: React.FC<StepTwoProps> = ({
    onNext,
    onBack,
    formulation,
    generatedCode,
    isLoading = false,
    processingMessage = ''
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newFiles = Array.from(event.target.files || []);
        setUploadedFiles(prevFiles => [...prevFiles, ...newFiles]);
        // Clear the input value so the same file can be selected again if needed
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRemoveFile = (indexToRemove: number) => {
        setUploadedFiles(files => files.filter((_, index) => index !== indexToRemove));
    };

    const formattedFormulation = formatFormulation(formulation);

    console.log(formattedFormulation);


    const UploadView = () => (
        <div className={styles.uploadContainer}>
            <div
                className={styles.dashedBox}
                onClick={() => fileInputRef.current?.click()}
                role="button"
                tabIndex={0}
            >
                <UploadIcon />
                <div className={styles.uploadTextContainer}>
                    <p className={styles.uploadText}>DOCs</p>
                    <p className={styles.formatText}>
                        Formats: .pdf, .docx, .xlx<br />
                        *Up to 10 data-docs
                    </p>
                </div>
            </div>
            <div className={styles.dividerContainer}>
                <div className={styles.dividerLine} />
                <p className={styles.orText}>OR</p>
                <div className={styles.dividerLine} />
            </div>
            <div className={styles.buttonContainer}>
                <button className={styles.awsButton}>
                    <AWSLogo /> Continue with AWS
                </button>
                <button className={styles.awsButton}>
                    <AzureLogo /> Continue with Azure
                </button>
                <button className={styles.awsButton}>
                    <SalesforceLogo /> Continue with Salesforce
                </button>
            </div>
        </div>
    );

    const FilesView = () => (
        <div className={styles.filesView}>
            <div className={styles.filesGrid}>
                {uploadedFiles.map((file, index) => (
                    <div key={file.name + index} className={styles.fileCard}>
                        <div className={styles.fileNameContainer}>
                            <DownloadFile />
                            <p className={styles.fileName}>{file.name}</p>
                        </div>
                    </div>
                ))}
                <div
                    className={`${styles.dashedBox} ${styles.uploadMore}`}
                    onClick={() => fileInputRef.current?.click()}
                    role="button"
                    tabIndex={0}
                >
                    <UploadIcon />
                    <div className={styles.uploadTextContainer}>
                        <p className={styles.uploadText}>DOCs</p>
                        <p className={styles.formatText}>
                            Formats: .pdf, .docx, .xlx<br />
                            *Up to 10 data-docs
                        </p>
                    </div>
                </div>
            </div>

            <div className={styles.divider} />
            <button
                className={styles.continueButton}
                onClick={() => onNext(uploadedFiles, "Optimization Code")}
                disabled={uploadedFiles.length === 0}
            >
                Continue
            </button>
        </div>
    );

    const LoadingView = () => (
        <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <p className={styles.loadingMessage}>{processingMessage || 'Loading formulation...'}</p>
        </div>
    );

    return (
        <Layout>
            <div className={styles.container}>
                <div className={styles.leftPanel}>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        accept=".pdf,.docx,.xlx,.csv"
                        multiple
                        className={styles.hiddenInput}
                    />
                    <div className={styles.header}>
                        <div className={styles.backButton} role="button" onClick={() => onBack()}>
                            <ArrowLeft />
                            <p className={styles.stepText}>Go back</p>
                        </div>
                        <p className={styles.stepText}>Step 2 of 4</p>
                    </div>
                    <div className={styles.titleContainer}>
                        <p className={styles.title}>Upload data</p>
                        <p className={styles.subtitle}>Boilerplate text about the data</p>
                    </div>
                    {uploadedFiles.length > 0 ? <FilesView /> : <UploadView />}
                </div>
                <div className={styles.rightPanel}>
                    <Logo />
                    {isLoading ? (
                        <LoadingView />
                    ) : formattedFormulation ? (
                        <div className={styles.formulationContainer}>
                            {formattedFormulation.latexData && (
                                <div className={styles.codeSection}>
                                    <h3 className={styles.subsectionTitle}>LaTeX Formulation</h3>
                                    <BlockMath math={formattedFormulation.latexData} />
                                </div>
                            )}
                            <h2 className={styles.sectionTitle}>{formattedFormulation.title}</h2>
                            <p className={styles.description}>{formattedFormulation.description}</p>

                            <h3 className={styles.subsectionTitle}>Decision Variables</h3>
                            <ul className={styles.variablesList}>
                                {formattedFormulation.variables.map((variable, index) => (
                                    <li key={index} className={styles.variableItem}>
                                        <InlineMath math={variable.name} /> : {variable.definition}
                                    </li>
                                ))}
                            </ul>

                            <h3 className={styles.subsectionTitle}>Parameters</h3>
                            <ul className={styles.parametersList}>
                                {formattedFormulation.parameters.map((parameter, index) => (
                                    <li key={index} className={styles.parameterItem}>
                                        <InlineMath math={parameter.name} /> : {parameter.definition}
                                    </li>
                                ))}
                            </ul>

                            <h3 className={styles.subsectionTitle}>Constraints</h3>
                            <ul className={styles.constraintsList}>
                                {formattedFormulation.constraints.map((constraint, index) => (
                                    <li key={index} className={styles.constraintItem}>
                                        <p className={styles.constraintTitle}>{constraint.title}</p>
                                        <p className={styles.constraintDescription}>{constraint.description}</p>
                                        <div className={styles.constraintFormulation}>
                                            <BlockMath math={constraint.formula} />
                                        </div>
                                    </li>
                                ))}
                            </ul>

                            <h3 className={styles.subsectionTitle}>{formattedFormulation.objective.title}</h3>
                            <p className={styles.objectiveDescription}>{formattedFormulation.objective.description}</p>
                            <div className={styles.objectiveFormulation}>
                                <BlockMath math={formattedFormulation.objective.formula} />
                            </div>

                            {formattedFormulation.jsonData && (
                                <div className={styles.codeSection}>
                                    <h3 className={styles.subsectionTitle}>JSON Representation</h3>
                                    <SyntaxHighlighter language="json" style={oneDark} customStyle={{ borderRadius: '8px' }}>
                                        {formattedFormulation.jsonData}
                                    </SyntaxHighlighter>
                                </div>
                            )}

                            {formattedFormulation.latexData && (
                                <div className={styles.codeSection}>
                                    <h3 className={styles.subsectionTitle}>LaTeX Representation</h3>
                                    <SyntaxHighlighter language="latex" style={oneDark} customStyle={{ borderRadius: '8px' }}>
                                        {formattedFormulation.latexData}
                                    </SyntaxHighlighter>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className={styles.emptyState}>
                            <p>No formulation available. Please go back and create a formulation first.</p>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default StepTwo;
